import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { apiService } from '../../services';
// no longer need programmatic submit
import { fixID } from '../../helpers';
import { UpdateError, CollectPaymentError, ExelotError } from '../../helpers/fetchErrors';
import { PaymentPageOpts } from '../../helpers/payments';
import { ReduxFormSmsID } from '../../components/Parcel';
import { Loading } from '../../components/GUI';
import { Page404 } from '../Page404';

const MissingID = (props) => {
  const {
    history,
    getMissingIDParcelAPI,
    updateMissingIDParcelAPI,
    collectMissingIDParcelAPI,
    match: { params }
  } = props;

  const [parcelId, setParcelId] = useState(null);
  const [parcelData, setParcelData] = useState(null);
  const [error, setError] = useState();
  const [creditGuardUrl, setCreditGuardUrl] = useState();
  const [submitted, setSubmitted] = useState(false);
  const [savedValues, setSavedValues] = useState(null);

  const savedValuesRef = useRef(null);

  // retry config for update after payment
  const UPDATE_MAX_RETRIES = 2; // number of retries on top of first attempt
  const UPDATE_RETRY_DELAY_MS = 200; // delay between retries in milliseconds

  const attemptUpdateWithRetries = (values) => {
    let attempts = 0;
    const maxAttempts = UPDATE_MAX_RETRIES + 1; // total attempts

    const tryOnce = () =>
      updateValues(values).catch((err) => {
        attempts += 1;
        if (attempts >= maxAttempts) {
          throw err;
        }
        return new Promise((resolve) => setTimeout(resolve, UPDATE_RETRY_DELAY_MS)).then(tryOnce);
      });

    return tryOnce();
  };

  useEffect(() => {
    function handleCGPaymentCompletion(evt) {
      const { data } = evt;
      if (!data) return;
      const { status, page, res } = data;
      if (PaymentPageOpts[page] !== PaymentPageOpts.missingID) {
        return;
      }

      // only when success we should update the email and ID
      if (status === 'success') {
        const values = savedValuesRef.current;
        if (values) {
          // update with saved values before redirect using shared helper with retries
          attemptUpdateWithRetries(values)
            .then(() => {
              history.push(`/missingID/callback/${status}`, { ...res, type: 'payment' });
            })
            .catch(() => {
              setError(new UpdateError().message);
            });
        } else {
          history.push(`/missingID/callback/${status}`, { ...res, type: 'payment' });
        }

        return;
      }

      // on error or cancelled just redirect
      history.push(`/missingID/callback/${status}`, { ...res });
    }

    window.addEventListener('message', handleCGPaymentCompletion, false);
    return () => window.removeEventListener('message', handleCGPaymentCompletion, false);
  }, [history, parcelId, updateMissingIDParcelAPI]);

  useEffect(() => {
    const id = fixID(params.parcelID);
    if (!id) {
      setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      return;
    }

    setParcelId(id);

    apiService
      .callServer(getMissingIDParcelAPI, { ':id:': id }, null, null, null, true)
      .then(res => setParcelData(res))
      .catch(() => setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.'));
  }, []);

  function updateValues(values) {
    return apiService
      .callServer(
        updateMissingIDParcelAPI,
        { ':id:': parcelId },
        null,
        JSON.stringify(values),
        null,
        true
      )
      .catch(() => {
        throw new UpdateError();
      });
  }

  function collectPayment() {
    const baseCreditGuardCallbackUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/parcels/creditguard/process_payment?page=missingID&status=:status:`;
    const creditGuardCallbackUrls = {
      success_url: baseCreditGuardCallbackUrl.replace(':status:', 'success'),
      error_url: baseCreditGuardCallbackUrl.replace(':status:', 'error'),
      cancel_url: baseCreditGuardCallbackUrl.replace(':status:', 'cancelled')
    };

    return apiService
      .callServer(
        collectMissingIDParcelAPI,
        { ':id:': parcelId },
        null,
        JSON.stringify({ ...creditGuardCallbackUrls }),
        null,
        true
      )
      .then(res => setCreditGuardUrl(res.url))
      .catch(() => {
        throw new CollectPaymentError();
      });
  }

  const submit = (values) => {
    if (!parcelData) return;

    // price 0 -> update immediately and redirect to callback
    if (!parcelData.price || parcelData.price === 0) {
      setSubmitted(true);
      updateValues(values)
        .then(() => {
          history.push(`/missingID/callback/success`, { hawb: parcelData.hawb, type: 'update' });
        })
        .catch((err) => {
          setError((err && err.message) || new UpdateError().message);
          setSubmitted(true);
        });

      return;
    }

    // price > 0 -> initiate payment, save values for post-payment update
    setSavedValues(values);
    savedValuesRef.current = values;
    setSubmitted(true);
    collectPayment().catch((err) => {
      if (err instanceof ExelotError) {
        setError(err.message);
        return;
      }

      console.error(err);
      setError(new CollectPaymentError().message);
    });
  };

  function renderError() {
    if (!error) return null;
    return (
      <div className='error-message'>{error}</div>
    );
  }

  if (error && !parcelData) return renderError();
  if (!parcelData) return <Loading />;

  if (creditGuardUrl) {
    // show iframe for payment
    // lazy import to avoid adding another dependency; simple iframe
    return (
      <iframe title='cg' src={creditGuardUrl} className='cg-iframe' />
    );
  }

  // normal form view
  return (
    <div style={{ direction: 'rtl', width: '310px', margin: '0 auto' }}>
      {renderError()}
      {(parcelData.hoursLeft !== null && parcelData.hoursLeft > 0)
        ? (
            <ReduxFormSmsID
              onSubmit={submit}
              parcel={parcelData.description}
              buyer={parcelData.buyerName}
              submitLabel={(parcelData.price && parcelData.price > 0) ? 'המשך לתשלום' : 'אישור'}
              price={parcelData.price}
              priceDescription={parcelData.priceDescription}
            />
       ) : (
            <div className='sms-content-wrap'>
              <h2 className='sms-message'>
                לקוח/ה יקר/ה,<br />
                לצערנו חלון הזמן לביצוע עדכון תעודת הזהות הסתיים.
              </h2>
            </div>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const { getMissingIDParcel, updateMissingIDParcel, collectMissingIDParcel } = state.api;
  return {
    getMissingIDParcelAPI: getMissingIDParcel,
    updateMissingIDParcelAPI: updateMissingIDParcel,
    collectMissingIDParcelAPI: collectMissingIDParcel
  };
}

const connected = withRouter(connect(mapStateToProps)(MissingID));
export { connected as MissingID };
